import React from 'react';
import {
  Box,
  Flex,
  colors,
  spacing,
  useI18nContext,
  Typography,
} from '@procore/core-react';
import PT from 'prop-types';
import styled from 'styled-components';

import { ADDITIONAL_CATEGORY_LINKS } from '@/react/consts';
import { UnstyledLink } from '@/react/styles';
import { marketplaceColors } from '@/react/styles/constants';
import { isLinkActive } from '@/react/shared/utils/helpers';

const StyledSidebar = styled(Box)`
  background-color: transparent;

  @media (max-width: 1024px) {
    padding: ${spacing.lg}px ${spacing.xl}px;
  }
`;

const Divider = styled(Box)`
  margin: ${spacing.xl}px;
  border-bottom: 1px solid ${colors.gray90};
  height: 1px;
`;

const MenuItem = styled(Flex).attrs({ className: 'menu-item' })`
  font-family: inherit;
  font-size: 14px;
  color: ${({ isProfessional }) =>
    isProfessional ? colors.black : colors.gray15};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease-in-out 0s;
  border-radius: 0 4px 4px 0;
  padding: 6px ${spacing.sm}px 6px ${spacing.md}px;
  margin: 0;
  border-left: ${spacing.xs}px solid transparent;
  border-left-color: ${({ isProfessional, active }) =>
    active ? (isProfessional ? colors.black : colors.orange50) : 'transparent'};
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 20px;
  font-weight: ${({ isProfessional, active }) =>
    active ? (isProfessional ? '600' : '400') : '400'};
  background-color: ${({ isProfessional, active }) =>
    active
      ? isProfessional
        ? marketplaceColors.stone50
        : colors.gray90
      : 'transparent'};

  &:hover {
    background-color: ${({ isProfessional }) =>
      isProfessional ? marketplaceColors.stone40 : colors.gray90};
    border-left-color: ${({ isProfessional }) =>
      isProfessional ? marketplaceColors.divider : colors.orange50};
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const FlexMenuContainer = styled(Flex)`
  flex-direction: column;
  & > * {
    width: 100%;
  }
`;

const ListingsSidebar = ({ categories, professionalMarketplace, onClose }) => {
  const isProfessional = professionalMarketplace;
  const i18n = useI18nContext();

  // Filter out 'built-by' link when not in professional marketplace
  const filteredCategoryLinks = ADDITIONAL_CATEGORY_LINKS.filter((category) => {
    if (category.slug === 'built-by' && !isProfessional) {
      return false;
    }
    return true;
  });

  return (
    <StyledSidebar>
      <FlexMenuContainer direction="column">
        {filteredCategoryLinks.map((category) => (
          <UnstyledLink
            data-testid={`category-${i18n.t(category.title)}`}
            href={category.href}
            key={category.slug}
            onClick={onClose ? () => onClose() : null}
          >
            <MenuItem
              alignItems="center"
              active={isLinkActive(category.slug)}
              isProfessional={isProfessional}
            >
              {i18n.t(category.title)}
            </MenuItem>
          </UnstyledLink>
        ))}

        {!isProfessional && <Divider />}

        <Box padding="lg 0">
          <Typography intent="h2" weight="bold">
            {i18n.t('filters.categories')}
          </Typography>
        </Box>
        {categories.map((category) => (
          <UnstyledLink
            href={`/${category.type === 'region' ? 'regions' : 'categories'}/${
              category.slug
            }`}
            key={category.slug}
            data-testid={`category-${category.slug}`}
          >
            <MenuItem
              padding="sm xl"
              alignItems="center"
              active={isLinkActive(
                `/${category.type === 'region' ? 'regions' : 'categories'}/${
                  category.slug
                }`
              )}
              isProfessional={isProfessional}
            >
              {category.name}
            </MenuItem>
          </UnstyledLink>
        ))}
      </FlexMenuContainer>
    </StyledSidebar>
  );
};

ListingsSidebar.propTypes = {
  categories: PT.arrayOf(
    PT.shape({
      name: PT.string.isRequired,
      slug: PT.string.isRequired,
      type: PT.string.isRequired,
    })
  ).isRequired,
  professionalMarketplace: PT.bool,
  onClose: PT.func,
};

export default ListingsSidebar;
